// KPIs › Scorecard's pure rules (KPIS-MODE, founder-approved 2026-10-22): everything the grid
// decides without React, so each rule has a unit test (scorecard-model.test.ts).
//   - cells: a period's mark (`gridCell`, hit / near / miss under the metric's bands), the 13
//     columns of a grain (weeks, months or quarters) and their headings;
//   - entry: why a cell takes no number (`cellBlockReason`, the cell's tooltip), and whether a
//     change is a correction that needs its reason (`correctionNeedsReason`);
//   - rows: seats grouped by team (`groupByTeam`), a seat with its branch (`branchOf`), the
//     filters' intersection (`keepSeat`), the off-track rows (`isOffTrack`), a scoped viewer's
//     per-seat cards as grid seats (`childOf`);
//   - history: a cell's observations, newest first, with their source and state (`historyRows`).
// Which seats a viewer may see is never decided here: it is the server's `kpi_visible_seat_ids`.
import { attainment, DEFAULTS } from "@companyos/domain-kpi";
import type { ScoreMarkState } from "@companyos/ui";
import type {
  MetricDef,
  Observation,
  Scorecard,
  ScorecardChild,
  ScorecardMetric,
} from "../../api/types";
import { shiftWeek } from "../../focus/weeks";
import { agentReportText } from "../agent-report";
import { formatHours, formatMetricValue, formatTarget } from "../format";
import { hitMiss } from "../hit-miss";
import { healthMark, metricHealth } from "../kpi/health";

/** The grid's columns: weeks (the default), months or quarters (`?cols=`). */
export type Grain = "week" | "month" | "quarter";

/** How many periods the grid shows (the API's trend carries 13). */
export const GRID_COLUMNS = 13;

export const GRAIN_OPTIONS: { value: Grain; label: string }[] = [
  { value: "week", label: "Weeks" },
  { value: "month", label: "Months" },
  { value: "quarter", label: "Quarters" },
];

// Cells -------------------------------------------------------------------------------------------

export type GridCell = { state: ScoreMarkState; label: string; value: string | null };

/**
 * One cell: the metric's trend point for `key`, joined by `period_key` (never by position). A
 * period with no point is "No data". `between` needs the period's own upper bound, which the
 * trend does not carry: only the scorecard's own period may use `target_max`; earlier periods
 * are not judged rather than judged against today's range.
 */
export function gridCell(
  m: ScorecardMetric,
  key: string,
  period: string,
  currency: string,
): GridCell {
  const p = m.trend.find((t) => t.period_key === key);
  if (!p || p.actual === null) return { state: "none", label: "No data", value: null };
  const full = formatMetricValue(p.actual, m.unit, currency);
  // A column is narrow: time shows as decimal hours ("30.5 h"); the label keeps "30 h 30 min".
  const value = m.unit === "time" ? formatHours(p.actual) : full;
  if (p.target === null) return { state: "none", label: `${full}: no target`, value };
  if (m.direction === "between" && key !== period) {
    const unit = key.includes("-W") ? "week" : "period";
    return { state: "none", label: `${full}: range target not recorded for this ${unit}`, value };
  }
  // TEAM-KPI: the period's health under the metric's own bands (the company's when it has none):
  // green a hit, amber near the target, red a miss.
  const state = cellState(m, p.actual, p.target, key === period ? m.target_max : null);
  const verb = state === "hit" ? "Hit" : state === "near" ? "Near target" : "Missed";
  const target = formatTarget({ ...m, target: p.target }, currency);
  return { state, label: `${verb}: ${full} (target ${target})`, value };
}

/** One period's mark: hit / near / miss by the metric's bands over the direction's attainment. */
export function cellState(
  m: Pick<ScorecardMetric, "direction" | "bands">,
  actual: number,
  target: number,
  targetMax: number | null,
): ScoreMarkState {
  const met = hitMiss(m.direction, actual, target, targetMax);
  const att = attainment(m.direction, actual, target, DEFAULTS.attainment_cap, targetMax);
  const mark = healthMark(metricHealth(att, m.bands));
  // Without bands of its own a met target is a hit (the company's green starts at 100%).
  return !m.bands && met === "hit" ? "hit" : mark;
}

/** A period key `delta` periods away: "2026-W43", "2026-10", "2026-Q4". */
export function shiftPeriod(grain: Grain, key: string, delta: number): string {
  if (grain === "week") return shiftWeek(key, delta);
  const year = Number(key.slice(0, 4));
  if (grain === "month") {
    const i = year * 12 + Number(key.slice(5, 7)) - 1 + delta;
    return `${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, "0")}`;
  }
  const i = year * 4 + Number(key.slice(6)) - 1 + delta;
  return `${Math.floor(i / 4)}-Q${(i % 4) + 1}`;
}

/** The columns ending with `period`, oldest first. */
export function gridColumns(grain: Grain, period: string, count = GRID_COLUMNS): string[] {
  return Array.from({ length: count }, (_, i) => shiftPeriod(grain, period, i - (count - 1)));
}

/** The 13 week columns ending with the scorecard's period, oldest first. */
export function gridWeeks(period: string, count = GRID_COLUMNS): string[] {
  return gridColumns("week", period, count);
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** A column heading: "W42", "Oct", "Q4". */
export function columnHeading(grain: Grain, key: string): string {
  if (grain === "week") return `W${key.slice(6)}`;
  if (grain === "quarter") return key.slice(5);
  return MONTHS[Number(key.slice(5, 7)) - 1] ?? key;
}

/** The grid's caption line: "Last 13 weeks · click or Tab into a cell to enter a number". */
export function gridCaption(grain: Grain, canEdit: boolean, count = GRID_COLUMNS): string {
  const span = `Last ${count} ${grain === "week" ? "weeks" : grain === "month" ? "months" : "quarters"}`;
  return canEdit
    ? `${span} · click or Tab into a cell to enter a number`
    : `${span} · hit or miss per KPI`;
}

// Entry -------------------------------------------------------------------------------------------

/** What the grid needs of a metric definition (metrics.list, or a member's `me/numbers`). */
export type DefInfo = {
  source: MetricDef["source"];
  cadence: MetricDef["cadence"];
  archived: boolean;
};

export function defInfo(d: Pick<MetricDef, "source" | "cadence" | "archived_at">): DefInfo {
  return { source: d.source, cadence: d.cadence, archived: d.archived_at !== null };
}

const GRAIN_OF_CADENCE: Partial<Record<NonNullable<DefInfo["cadence"]>, Grain>> = {
  weekly: "week",
  monthly: "month",
  quarterly: "quarter",
};
const CADENCE_WORD: Record<Grain, string> = {
  week: "Weekly",
  month: "Monthly",
  quarter: "Quarterly",
};
const VIEW_WORD: Record<Grain, string> = { week: "Weeks", month: "Months", quarter: "Quarters" };

export const BLOCK = {
  team: "Team KPI — its value comes from the team; read-only here",
  company: "Company KPI — read-only here",
  unknown: "Read-only — this KPI's entry settings are not available",
  archived: "Archived metric — it takes no new numbers",
  event: "Fed by events — computed from ingested events",
  formula: "Formula — computed from other KPIs",
  permission: "You can't edit this seat's numbers",
  daily: "Daily KPI — enter it per day on Enter numbers",
  yearly: "Yearly KPI — enter it on Enter numbers",
  noCadence: "No cadence set — give the KPI a cadence under Setup to enter it here",
  future: "Future period",
  pending: "Agent-reported, pending review",
} as const;

export type CellContext = {
  /** The row's subject: only seat rows take numbers on the grid. */
  subject: "seat" | "team" | "company";
  def: DefInfo | undefined;
  grain: Grain;
  /** The cell's period key and the grain's current period (today's week, month or quarter). */
  column: string;
  current: string;
  /** A manager, or the seat's own person holder (MEMBER-ENTRY). */
  canEditSubject: boolean;
  /** The period of an agent value waiting for review on this row (`pending_review`). */
  pendingPeriod: string | null;
};

/** Why a cell takes no number here, or null when it does (the cell's tooltip). */
export function cellBlockReason(c: CellContext): string | null {
  if (c.subject === "team") return BLOCK.team;
  if (c.subject === "company") return BLOCK.company;
  if (!c.def) return c.canEditSubject ? BLOCK.unknown : BLOCK.permission;
  if (c.def.archived) return BLOCK.archived;
  if (c.def.source === "event") return BLOCK.event;
  if (c.def.source === "formula") return BLOCK.formula;
  if (!c.canEditSubject) return BLOCK.permission;
  if (c.def.cadence === "daily") return BLOCK.daily;
  if (c.def.cadence === "yearly") return BLOCK.yearly;
  if (!c.def.cadence) return BLOCK.noCadence;
  const native = GRAIN_OF_CADENCE[c.def.cadence];
  if (native && native !== c.grain)
    return `${CADENCE_WORD[native]} KPI: switch to ${VIEW_WORD[native]} to enter`;
  if (c.column > c.current) return BLOCK.future;
  if (c.pendingPeriod === c.column) return BLOCK.pending;
  return null;
}

/** A manager may type this metric's week value: entered by hand, weekly, not a future week. */
export function editableMetric(def: MetricDef | undefined, week: string, today: string): boolean {
  return (
    cellBlockReason({
      subject: "seat",
      def: def ? defInfo(def) : undefined,
      grain: "week",
      column: week,
      current: today,
      canEditSubject: true,
      pendingPeriod: null,
    }) === null
  );
}

/**
 * A changed value supersedes a live one: correcting a past period asks for its reason, and a
 * seat's holder (not a manager) needs one for any correction (the API's `needsReason`).
 */
export function correctionNeedsReason(c: {
  hadValue: boolean;
  column: string;
  current: string;
  manager: boolean;
}): boolean {
  if (!c.hadValue) return false;
  return c.column < c.current || !c.manager;
}

/** The shortest reason the correction popover accepts (the API only wants it non-empty). */
export const MIN_REASON = 3;

export function reasonValid(text: string): boolean {
  return text.trim().length >= MIN_REASON;
}

// Rows --------------------------------------------------------------------------------------------

export type TeamLite = { id: string; name: string };

export type TeamGroup<T> = { team: TeamLite | null; seats: T[] };

/**
 * Seats under their team, teams A–Z, then the seats in no team ("No team"). A seat in several
 * teams sits under the first by name (one row per KPI, so the keyboard walk has one path). Teams
 * in `keep` stay as a group when no seat of theirs is shown (their own team KPIs are rows too).
 */
export function groupByTeam<T>(
  seats: readonly T[],
  seatId: (s: T) => string,
  teams: readonly TeamLite[],
  members: readonly { team_id: string; seat_id: string }[],
  keep: ReadonlySet<string> = new Set(),
): TeamGroup<T>[] {
  const sorted = [...teams].sort((a, b) => a.name.localeCompare(b.name));
  const rank = new Map(sorted.map((t, i) => [t.id, i]));
  const home = new Map<string, string>();
  for (const m of members) {
    const r = rank.get(m.team_id);
    if (r === undefined) continue;
    const prev = home.get(m.seat_id);
    if (prev === undefined || r < (rank.get(prev) ?? Infinity)) home.set(m.seat_id, m.team_id);
  }
  const groups: TeamGroup<T>[] = sorted.map((team) => ({ team, seats: [] }));
  const none: TeamGroup<T> = { team: null, seats: [] };
  for (const s of seats) {
    const t = home.get(seatId(s));
    const g = t === undefined ? none : groups[rank.get(t) ?? -1];
    (g ?? none).seats.push(s);
  }
  return [
    ...groups.filter((g) => g.seats.length > 0 || (g.team && keep.has(g.team.id))),
    ...(none.seats.length ? [none] : []),
  ];
}

/** The seat and every seat under it (its reports, theirs…), by `supervisor_seat_id`. */
export function branchOf(
  seatId: string,
  seats: readonly { id: string; supervisor_seat_id: string | null }[],
): Set<string> {
  const kids = new Map<string, string[]>();
  for (const s of seats)
    if (s.supervisor_seat_id)
      kids.set(s.supervisor_seat_id, [...(kids.get(s.supervisor_seat_id) ?? []), s.id]);
  const out = new Set([seatId]);
  const todo = [...(kids.get(seatId) ?? [])];
  while (todo.length) {
    const id = todo.pop() as string;
    if (out.has(id)) continue;
    out.add(id);
    todo.push(...(kids.get(id) ?? []));
  }
  return out;
}

/** A seat passes the filters when every active one (a non-null set) holds it. */
export function keepSeat(id: string, filters: readonly (ReadonlySet<string> | null)[]): boolean {
  return filters.every((f) => f === null || f.has(id));
}

/** Off track this period: its current cell is missed or near the target. */
export function isOffTrack(m: ScorecardMetric, period: string, currency: string): boolean {
  const s = gridCell(m, period, period, currency).state;
  return s === "miss" || s === "near";
}

/** A scoped viewer's seat card (`seats.scorecard`) as a grid seat, like a roll-up child. */
export function childOf(card: Scorecard): ScorecardChild {
  return {
    subject: card.subject,
    kpi_score: card.kpi_score,
    goal_score: card.goal_score,
    completeness: card.completeness,
    band: card.band,
    weight: 1,
    metrics: card.metrics,
  };
}

// History -----------------------------------------------------------------------------------------

export const SOURCE_LABELS: Record<Observation["source"], string> = {
  manual: "Entered by hand",
  import: "Imported",
  event: "From events",
  agent: "Agent-reported",
  ledger: "From the cost ledger",
  task: "Counted from a board's tasks",
};

export type HistoryState = "live" | "superseded" | "deleted";

export type HistoryRow = {
  id: string;
  value: number | null;
  at: string;
  who: string;
  source: string;
  note: string | null;
  state: HistoryState;
  /** The agent's provenance line ("Reported by Riley for 2026-W43 (run …): “…”"). */
  provenance: string | null;
  evidenceUrl: string | null;
};

/** Every row of a cell, newest first: who, how, why, and whether a later row replaced it. */
export function historyRows(
  rows: readonly Observation[],
  names: ReadonlyMap<string, string>,
): HistoryRow[] {
  const replaced = new Set(rows.flatMap((r) => (r.supersedes_id ? [r.supersedes_id] : [])));
  return [...rows]
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
    .map((r) => {
      const member = r.entered_by_membership_id ?? r.actor_membership_id;
      const who =
        (member ? names.get(member) : undefined) ??
        r.provenance?.agent_name ??
        (r.actor_kind === "system" ? "System" : member ? "A teammate" : "System");
      return {
        id: r.id,
        value: r.value === null ? null : Number(r.value),
        at: r.created_at,
        who,
        source: SOURCE_LABELS[r.source],
        note: r.note,
        state: r.tombstone ? "deleted" : replaced.has(r.id) ? "superseded" : "live",
        provenance: r.provenance
          ? agentReportText({
              period_key: r.period_key,
              value: Number(r.value ?? 0),
              source: r.source,
              recorded_at: r.created_at,
              provenance: r.provenance,
            })
          : null,
        evidenceUrl: r.provenance?.evidence_url ?? null,
      };
    });
}
